import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiClient } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Search, Filter, ShoppingCart, Heart } from "lucide-react";

const BeWear = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [subcategory, setSubcategory] = useState("all");

  useEffect(() => {
    fetchProducts();
  }, [subcategory]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let url = "/products?category=wear";
      if (subcategory && subcategory !== "all") {
        url += `&subcategory=${subcategory}`;
      }
      const response = await apiClient.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error("Products fetch error:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const addToCart = async (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await apiClient.post("/cart/add", {
        product_id: product.product_id,
        quantity: 1,
        size: product.sizes?.[0] || null,
        color: product.colors?.[0] || null,
      });
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="be-wear-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <span className="text-white font-heading font-bold text-xl">BE</span>
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">Wear</h1>
          </div>
          <p className="text-muted-foreground">
            Fashion apparel designed for the modern African lifestyle.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 mb-8"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={subcategory} onValueChange={setSubcategory}>
            <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="tshirt">T-Shirts</SelectItem>
              <SelectItem value="hoodie">Hoodies</SelectItem>
              <SelectItem value="joggers">Joggers</SelectItem>
              <SelectItem value="footwear">Footwear</SelectItem>
              <SelectItem value="accessories">Accessories</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-muted rounded-2xl mb-4"></div>
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-4">No products found</p>
            <Button onClick={() => { setSearchQuery(""); setSubcategory("all"); }}>
              Clear Filters
            </Button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          >
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.product_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/product/${product.product_id}`}
                  className="group block product-card"
                  data-testid={`product-card-${product.product_id}`}
                >
                  <div className="relative aspect-square rounded-2xl overflow-hidden mb-4 bg-muted">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <Button
                        size="sm"
                        className="rounded-full"
                        onClick={(e) => addToCart(product, e)}
                        data-testid={`add-to-cart-${product.product_id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                    </div>
                    <button
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => { e.preventDefault(); toast.success("Added to wishlist"); }}
                    >
                      <Heart className="w-4 h-4" />
                    </button>
                    {!product.in_stock && (
                      <Badge className="absolute top-3 left-3" variant="destructive">
                        Out of Stock
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Badge variant="outline" className="text-xs">
                      {product.subcategory}
                    </Badge>
                    <h3 className="font-medium truncate">{product.name}</h3>
                    <p className="text-primary font-heading font-bold text-lg">
                      ${product.price.toFixed(2)}
                    </p>
                    {product.colors?.length > 0 && (
                      <div className="flex gap-1">
                        {product.colors.slice(0, 4).map((color, i) => (
                          <div
                            key={i}
                            className="w-4 h-4 rounded-full border border-border"
                            style={{
                              backgroundColor:
                                color.toLowerCase() === "black"
                                  ? "#000"
                                  : color.toLowerCase() === "white"
                                  ? "#fff"
                                  : color.toLowerCase() === "orange"
                                  ? "#f97316"
                                  : color.toLowerCase() === "grey"
                                  ? "#6b7280"
                                  : color.toLowerCase() === "navy"
                                  ? "#1e3a5f"
                                  : color.toLowerCase() === "green"
                                  ? "#10b981"
                                  : color.toLowerCase() === "gold"
                                  ? "#fbbf24"
                                  : "#d1d5db",
                            }}
                            title={color}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

export default BeWear;
